import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretRightOutlined, CheckOutlined, MobileOutlined } from '@ant-design/icons';
import { Alert, Collapse, Flex, Radio, Switch } from 'antd';
import { collect } from 'collect.js';
import FadeIn from '../FadeIn';
import useHelpers from '../../hooks/context/useHelpers';
import { applyContractDiscount } from '../../services/helpers/ContractDiscountHelpers';

function Feature({ text }) {
  return (
    <p className="text-sm">
      <CheckOutlined className="text-brand-primary mr-sm" />
      {text}
    </p>
  );
}

/**
 * Struck original price + "-X%" badge, shown beside the discounted amount when
 * an annual-contract discount applies. Renders nothing without a discount or
 * for a free (0 €) line so we never show "-25%" next to nothing.
 */
function DiscountTag({ amount, discount, currency }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Billing:MobileAppProduct' });

  const { formatCurrency } = useHelpers();

  if (!discount || !amount) {
    return null;
  }

  return (
    <>
      <span className="line-through opacity-60">{formatCurrency(amount, currency)}</span>
      <span className="rounded border border-solid border-green-200 bg-green-50 px-[6px] py-[1px] text-xs font-semibold text-green-700">
        {t('labels.discount_badge', { percentage: discount })}
      </span>
    </>
  );
}

function SetupFeeOption({ price, description, discount, isSelected, onClick }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Billing:MobileAppProduct' });

  const { formatCurrency } = useHelpers();

  return (
    <div
      onClick={onClick}
      className="bg-gray-50 border-gray-200 transition-colors ease-in-out duration-300 border-solid p-sm rounded-lg cursor-pointer hover:bg-gray-100"
    >
      <Flex gap="small" justify="space-between">
        <Flex gap="small">
          <Radio checked={isSelected} className="my-auto" />
          <div>
            <p className="font-medium">
              {formatCurrency(applyContractDiscount(price, discount))} {t('labels.one_time_fee')}
            </p>
            <p className="text-gray-600 text-sm">{description}</p>
          </div>
        </Flex>
        <Flex gap="small" align="center" className="my-auto">
          <DiscountTag amount={price} discount={discount} />
        </Flex>
      </Flex>
    </div>
  );
}

export default function MobileAppProduct({
  data,
  isSelected,
  setupFee,
  onToggle,
  onSetupFeeSelected,
  disabled,
  discount = 0,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Billing:MobileAppProduct' });

  const { formatCurrency } = useHelpers();

  const setupFeeOptions = useMemo(
    () => [
      { price: 150, description: t('setup_fee_options.150') },
      { price: 75, description: t('setup_fee_options.75') },
      { price: 0, description: t('setup_fee_options.0') },
    ],
    [t],
  );
  const selectedSetupFeeOption = useMemo(
    () => collect(setupFeeOptions).firstWhere('price', setupFee),
    [setupFeeOptions, setupFee],
  );

  return (
    <div className="w-full md:max-w-[90%] lg:max-w-[60%] bg-white shadow rounded-lg p-lg flex flex-col relative space-y-md shadow">
      {disabled && (
        <Alert
          showIcon
          title={t('feedback.contract_signed_already.title')}
          description={t('feedback.contract_signed_already.description')}
        />
      )}
      <Flex justify="space-between">
        <Flex gap="middle">
          <div className="hidden md:flex bg-brand-primary/25 text-brand-primary text-lg justify-center items-center size-12 aspect-square rounded-xl">
            <MobileOutlined />
          </div>
          <div>
            <h3 className="leading-[2rem] font-semibold">{data.name}</h3>
            {data?.description && <p className="mb-0 whitespace-pre-line mt-sm text-gray-600">{data?.description}</p>}
          </div>
        </Flex>
        <Switch disabled={disabled} checked={isSelected} onChange={(checked) => onToggle(checked)} />
      </Flex>
      <span className="inline-flex items-center gap-xs">
        <h2 className="leading-[2.25rem] font-semibold">
          {formatCurrency(applyContractDiscount(data.price.amount, discount), data.price.currency)}
        </h2>
        <span className="mt-auto h-fit text-gray-600">/{t('labels.month')}</span>
        <DiscountTag amount={data.price.amount} discount={discount} currency={data.price.currency} />
      </span>
      <div className="space-y-sm">
        <Feature text={t('features.app')} />
        <Feature text={t('features.push_notifications')} />
        <Feature text={t('features.loyalty')} />
        <Feature text={t('features.offline_orders')} />
        <Feature text={t('features.individual_design')} />
      </div>
      {isSelected && (
        <FadeIn className="space-y-sm">
          <hr className="mb-md mt-none" />
          <h6>{t('sections.setup_fee.title')}</h6>
          <p className="text-sm text-gray-600">
            {t('sections.setup_fee.description', { amount: formatCurrency(applyContractDiscount(150, discount)) })}
          </p>
          {selectedSetupFeeOption && (
            <div className="bg-gray-100 border-gray-200 border-solid p-sm rounded-lg">
              <Flex justify="space-between" gap="small">
                <div>
                  <p className="font-medium">
                    {formatCurrency(applyContractDiscount(selectedSetupFeeOption.price, discount))}{' '}
                    {t('labels.one_time_fee')}
                  </p>
                  <p className="text-gray-600 text-sm">{selectedSetupFeeOption.description}</p>
                </div>
                <Flex gap="small" align="center" className="my-auto">
                  <DiscountTag amount={selectedSetupFeeOption.price} discount={discount} />
                </Flex>
              </Flex>
            </div>
          )}
          {!disabled && (
            <Collapse
              ghost
              expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
              items={[
                {
                  key: 'more_options',
                  classNames: {
                    header: '!p-none text-sm !text-gray-600',
                    body: '!p-none',
                  },
                  label: t('labels.more_options'),
                  children: (
                    <div className="space-y-md mt-md">
                      {setupFeeOptions.map((item, index) => (
                        <SetupFeeOption
                          price={item.price}
                          description={item.description}
                          discount={discount}
                          isSelected={item.price === selectedSetupFeeOption?.price}
                          onClick={() => onSetupFeeSelected(item.price)}
                          key={`setup-fee-option-${index}`}
                        />
                      ))}
                    </div>
                  ),
                },
              ]}
            />
          )}
        </FadeIn>
      )}
    </div>
  );
}
